import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Bar,
  BarChart as RcBarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip as RcTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  ArrowUpDown,
  CheckCircle2,
  ClipboardList,
  FileText,
  GripVertical,
  Loader2,
  Lock,
  Pencil,
  Plus,
  RefreshCcw,
  RotateCcw,
  Save,
  ShieldCheck,
  Trash2,
  Unlink,
  XCircle,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  toast,
} from "@eduviaos/ui-kit";
import { AIAssistDrawer, LastSuggestionPanel, PromptInputButton } from "@eduviaos/capability-ai-ui";
import type { CapabilityAiAssist } from "@eduviaos/capability-ai-ui";
import type { ApiError, CapabilityPromptContextWire } from "@eduviaos/shared";
import type { FormDefinition } from "@eduviaos/shared/types";
import { useFormsList } from "@/hooks/useForms";
import { useCanEditAdmissions } from "@/hooks/useAdmissionsAuthoring";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useIaCapabilityAiAssist } from "@/hooks/useCapabilityAiAssist";
import { TenantAdmissionsPausedBanner } from "@/components/TenantAdmissionsPausedBanner";
import { asTenantVertical, funnelCopy } from "./funnelCopy";
import {
  REQUIREMENT_KIND_HELP,
  REQUIREMENT_KIND_LABELS,
  REQUIREMENT_KIND_LIVE_EVALUATOR,
  admissionStagesKeyPrefix,
  type AdmissionFunnelResponse,
  type AdmissionStageItem,
  type AdmissionStagesResponse,
  type LinkedFormSummary,
  type StageRequirementKind,
  type StageRequirementWire,
  type WriteAdmissionStageBody,
  deriveRequirementKey,
  deriveStageKey,
  resolveStageColor,
  useAdmissionFunnel,
  useAdmissionStages,
  useLinkStageForm,
  useResetAdmissionStages,
  useWriteAdmissionStages,
} from "@/lib/admissionStages/api";

/**
 * Phase F-finale-6 Slice 6b — IA tenant-self admission-funnel editor.
 *
 * The IA counterpart to the SA `AdmissionsPanel`. Lives at
 * `/admissions/funnel` and is the only IA surface where a tenant admin
 * edits their own admission funnel (stages, policies, stage→form links,
 * action-gated requirements). Tenant is resolved BE-side from the
 * `X-Tenant-Slug` header + JWT, so it hits the bare `/admission-stages`
 * routes (NOT the SA cross-tenant routes).
 *
 * Write affordances are gated on `useCanEditAdmissions()`: when the SA
 * has paused editing (`pausedByTenant`) or the principal lacks
 * `manage_admissions` (`missingRbac`), the page renders a read-only view
 * with a banner — the funnel still drives the live admissions board, only
 * the write surface is gated.
 *
 * Slice 6c adds AI assist (L1 funnel / L2 stage / L3 requirement) via the
 * self-scoped `/ai/capability` routes — three triggers (funnel-policy card,
 * stages card) open the shared `AIAssistDrawer`, gated behind a clean draft
 * and `canEdit`. Applying mutates the tenant's server admission draft
 * (CAS-guarded by `draft_version`), then resyncs via the stages query.
 */
export function IaAdmissionsEditorPage() {
  const { canEditAdmissions, missingRbac, pausedByTenant } = useCanEditAdmissions();
  const stagesQuery = useAdmissionStages();
  const funnelQuery = useAdmissionFunnel();

  if (stagesQuery.isLoading) {
    return (
      <div className="text-muted-foreground inline-flex items-center gap-2 py-12 text-sm">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Loading admission funnel…
      </div>
    );
  }

  if (stagesQuery.isError || !stagesQuery.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Couldn&apos;t load your admission funnel</CardTitle>
          <CardDescription>
            {(stagesQuery.error as ApiError | undefined)?.message ??
              "The admission funnel API returned an unexpected error."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => stagesQuery.refetch()} variant="outline" size="sm">
            <RefreshCcw className="size-3.5" aria-hidden="true" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <AdmissionsEditorInner
      initial={stagesQuery.data}
      funnel={funnelQuery.data}
      funnelLoading={funnelQuery.isLoading}
      canEdit={canEditAdmissions}
      missingRbac={missingRbac}
      pausedByTenant={pausedByTenant}
    />
  );
}

// ── Draft model (mirrors SA AdmissionsPanel) ──────────────────────────

interface DraftRequirement {
  id: string;
  key: string | null;
  kind: StageRequirementKind;
  label: string;
  config: Record<string, unknown>;
}

interface DraftStage {
  id: string;
  key: string | null;
  label: string;
  is_terminal: boolean;
  terminal_outcome: "admitted" | "rejected" | null;
  color_hint: string | null;
  auto_advance_days: number | null;
  required_documents: string[];
  linked_form: LinkedFormSummary | null;
  requirements: DraftRequirement[];
}

interface DraftConfig {
  stages: DraftStage[];
  allow_direct_admission: boolean;
  allow_skip_stages: boolean;
  allow_revert: boolean;
  duplicate_detection: boolean;
}

function toDraft(stage: AdmissionStageItem): DraftStage {
  return {
    id: stage.key,
    key: stage.key,
    label: stage.label,
    is_terminal: stage.is_terminal,
    terminal_outcome: stage.terminal_outcome,
    color_hint: stage.color_hint,
    auto_advance_days: stage.auto_advance_days,
    required_documents: [...stage.required_documents],
    linked_form: stage.linked_form,
    requirements: stage.requirements.map((req) => ({
      id: req.key,
      key: req.key,
      kind: req.kind,
      label: req.label,
      config: { ...req.config },
    })),
  };
}

function toDraftConfig(res: AdmissionStagesResponse): DraftConfig {
  return {
    stages: res.stages.map(toDraft),
    allow_direct_admission: res.allow_direct_admission,
    allow_skip_stages: res.allow_skip_stages,
    allow_revert: res.allow_revert,
    duplicate_detection: res.duplicate_detection,
  };
}

interface AdmissionsEditorInnerProps {
  initial: AdmissionStagesResponse;
  funnel: AdmissionFunnelResponse | undefined;
  funnelLoading: boolean;
  canEdit: boolean;
  missingRbac: boolean;
  pausedByTenant: boolean;
}

function AdmissionsEditorInner({
  initial,
  funnel,
  funnelLoading,
  canEdit,
  missingRbac,
  pausedByTenant,
}: AdmissionsEditorInnerProps) {
  const [draft, setDraft] = React.useState<DraftConfig>(() => toDraftConfig(initial));
  const [serverError, setServerError] = React.useState<string | null>(null);

  // Re-seed the draft whenever the BE returns a fresh canonical config
  // (after Reset / Save). React-query updates the cache via setQueryData;
  // the new `initial` prop flows down here.
  const lastSyncedRef = React.useRef(initial);
  React.useEffect(() => {
    if (lastSyncedRef.current !== initial) {
      lastSyncedRef.current = initial;
      setDraft(toDraftConfig(initial));
    }
  }, [initial]);

  const writeMutation = useWriteAdmissionStages();
  const resetMutation = useResetAdmissionStages();
  const formsQuery = useFormsList();
  const forms = React.useMemo<TenantFormSummary[]>(
    () => (formsQuery.data?.items ?? []).map(toFormSummary),
    [formsQuery.data],
  );

  const dirty = React.useMemo(() => isDirty(draft, initial), [draft, initial]);

  // ── AI config-assist (Phase F-finale-6 Slice 6c) ────────────────────
  // Self-scoped assist for the funnel (L1), individual stages (L2), and
  // action-gated requirements (L3). The prompt context is assembled fresh
  // at request time from the *server* canonical config (`initial`), never
  // the local draft: assist is gated behind a clean draft (see
  // `startAssist`), so server == draft when a request fires, and the
  // `draft_version` we echo is the one the BE CAS-checks on apply. Tenant
  // is resolved BE-side from the IA `api` singleton header + JWT — the
  // `tenant_id` we send is informational only.
  const queryClient = useQueryClient();
  const modules = useEnabledModules();

  const buildContext = React.useCallback(
    (): CapabilityPromptContextWire => ({
      tenant_id: initial.tenant_slug,
      vertical: initial.vertical,
      enabled_modules: [...modules.enabledKeys],
      // The IA portal has no cross-tenant assessment-template query (that
      // is an SA-only surface). The BE validates any `assessment_attempt`
      // requirement's `template_slug` against the live tenant on apply, so
      // an empty hint list here only narrows the model's autocomplete.
      assessment_template_slugs: [],
      existing_stages: initial.stages.map((s) => ({
        key: s.key,
        label: s.label,
        is_terminal: s.is_terminal,
        terminal_outcome: s.terminal_outcome,
        requirements: s.requirements.map((r) => ({ key: r.key, kind: r.kind, label: r.label })),
      })),
      available_form_slugs: forms.map((f) => f.slug),
      draft_version: initial.draft_version,
    }),
    [initial, modules.enabledKeys, forms],
  );

  const onAiApplied = React.useCallback(() => {
    // The apply mutated the tenant's server draft — resync the editor. The
    // stages config + funnel report both live under this prefix key, so a
    // single prefix-match invalidation refetches everything the editor
    // reads; the re-seed effect above flows the fresh config into the
    // local draft.
    void queryClient.invalidateQueries({ queryKey: admissionStagesKeyPrefix });
    toast.success("AI changes applied to the admission draft.");
  }, [queryClient]);

  const funnelAssist = useIaCapabilityAiAssist({
    targetKind: "funnel",
    buildContext,
    onApplied: onAiApplied,
  });
  const stageAssist = useIaCapabilityAiAssist({
    targetKind: "stage",
    buildContext,
    onApplied: onAiApplied,
  });
  // L3 — requirement assist. A requirement attaches to a parent stage by
  // `parent_stage_key`; the admin names the target stage in the prompt and
  // the BE skips with a reason if that stage doesn't exist. Kept at the
  // editor level (not nested in the per-stage RequirementsDialog) so the
  // drawer (a Radix Sheet) never opens inside that dialog (a Radix Dialog),
  // which would double-trap focus.
  const requirementAssist = useIaCapabilityAiAssist({
    targetKind: "requirement",
    buildContext,
    onApplied: onAiApplied,
  });

  const startAssist = React.useCallback(
    (assist: CapabilityAiAssist) => {
      if (dirty) {
        toast.error("Save or discard your unsaved changes before using AI assist.");
        return;
      }
      assist.start();
    },
    [dirty],
  );

  const onSave = () => {
    setServerError(null);
    writeMutation.mutate(buildWriteBody(draft), {
      onError: (err) => setServerError(err.message ?? "Failed to save admission funnel."),
    });
  };

  const onDiscard = () => {
    setServerError(null);
    setDraft(toDraftConfig(initial));
  };

  const onReset = () => {
    setServerError(null);
    resetMutation.mutate(undefined, {
      onError: (err) => setServerError(err.message ?? "Failed to reset admission funnel."),
    });
  };

  return (
    <div className="grid gap-4">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderHeading>
            <PageHeaderTitle>Admission funnel</PageHeaderTitle>
            <PageHeaderDescription>
              {funnelCopy(asTenantVertical(initial.vertical)).description}
            </PageHeaderDescription>
          </PageHeaderHeading>
        </PageHeaderTop>
      </PageHeader>

      {missingRbac ? (
        <PermissionNotice />
      ) : pausedByTenant ? (
        <TenantAdmissionsPausedBanner />
      ) : null}

      <HeaderCard
        source={initial.source}
        vertical={initial.vertical}
        draft={draft}
        readOnly={!canEdit}
        onChangeFlag={(patch) => setDraft((d) => ({ ...d, ...patch }))}
        onReset={onReset}
        resetting={resetMutation.isPending}
        onAiAssist={canEdit ? () => startAssist(funnelAssist) : undefined}
      />

      {canEdit ? (
        <LastSuggestionPanel
          suggestion={funnelAssist.open ? null : funnelAssist.lastSuggestion}
          onReopen={funnelAssist.reopen}
        />
      ) : null}

      <FunnelChartCard funnel={funnel} loading={funnelLoading} draft={draft} />

      <StagesEditorCard
        draft={draft}
        setDraft={setDraft}
        forms={forms}
        formsLoading={formsQuery.isLoading}
        readOnly={!canEdit}
        onAiAssist={canEdit ? () => startAssist(stageAssist) : undefined}
        onAiAssistRequirement={canEdit ? () => startAssist(requirementAssist) : undefined}
      />

      {canEdit ? (
        <>
          <LastSuggestionPanel
            suggestion={stageAssist.open ? null : stageAssist.lastSuggestion}
            onReopen={stageAssist.reopen}
          />
          <LastSuggestionPanel
            suggestion={requirementAssist.open ? null : requirementAssist.lastSuggestion}
            onReopen={requirementAssist.reopen}
          />
        </>
      ) : null}

      {canEdit ? (
        <SaveBar
          dirty={dirty}
          saving={writeMutation.isPending}
          onSave={onSave}
          onDiscard={onDiscard}
          error={serverError}
        />
      ) : null}

      {canEdit ? (
        <>
          <AIAssistDrawer
            open={funnelAssist.open}
            onOpenChange={funnelAssist.setOpen}
            targetKind="funnel"
            state={funnelAssist.state}
            onGenerate={funnelAssist.generate}
            onApply={funnelAssist.apply}
            onReject={funnelAssist.reject}
            onRetry={funnelAssist.retry}
          />
          <AIAssistDrawer
            open={stageAssist.open}
            onOpenChange={stageAssist.setOpen}
            targetKind="stage"
            state={stageAssist.state}
            onGenerate={stageAssist.generate}
            onApply={stageAssist.apply}
            onReject={stageAssist.reject}
            onRetry={stageAssist.retry}
          />
          <AIAssistDrawer
            open={requirementAssist.open}
            onOpenChange={requirementAssist.setOpen}
            targetKind="requirement"
            state={requirementAssist.state}
            onGenerate={requirementAssist.generate}
            onApply={requirementAssist.apply}
            onReject={requirementAssist.reject}
            onRetry={requirementAssist.retry}
          />
        </>
      ) : null}
    </div>
  );
}

function PermissionNotice() {
  return (
    <div
      role="status"
      data-testid="admissions-missing-rbac-notice"
      className="border-border/60 bg-muted/30 text-muted-foreground flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs"
    >
      <Lock aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      <div className="flex-1 leading-relaxed">
        <span className="text-foreground font-medium">View-only access.</span> You don&apos;t have
        permission to edit the admission funnel. Ask an administrator to grant you the{" "}
        <code>manage_admissions</code> permission to make changes.
      </div>
    </div>
  );
}

// ── Header card (source + funnel policy + reset) ──────────────────────

function HeaderCard({
  source,
  vertical,
  draft,
  readOnly,
  onChangeFlag,
  onReset,
  resetting,
  onAiAssist,
}: {
  source: AdmissionStagesResponse["source"];
  vertical: string;
  draft: DraftConfig;
  readOnly: boolean;
  onChangeFlag: (patch: Partial<DraftConfig>) => void;
  onReset: () => void;
  resetting: boolean;
  onAiAssist?: () => void;
}) {
  const [confirmingReset, setConfirmingReset] = React.useState(false);
  const copy = funnelCopy(asTenantVertical(vertical));

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid gap-0.5">
            <CardTitle>Funnel policy</CardTitle>
            <CardDescription>
              How enquiries flow through the funnel. These apply immediately to new enquiries once
              saved.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {onAiAssist && (
              <PromptInputButton
                onClick={onAiAssist}
                label="AI assist"
                data-testid="ai-assist-funnel"
              />
            )}
            <Badge variant={source === "tenant" ? "default" : "outline"}>
              {source === "tenant" ? "Customised" : copy.defaultBadge}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-3">
        <div className="grid gap-2 sm:grid-cols-2">
          <PolicySwitch
            label="Allow direct admission"
            description="Enquiries can be marked admitted without traversing every intermediate stage."
            checked={draft.allow_direct_admission}
            disabled={readOnly}
            onCheckedChange={(v) => onChangeFlag({ allow_direct_admission: v })}
          />
          <PolicySwitch
            label="Allow skipping stages"
            description={copy.skipStagesHint}
            checked={draft.allow_skip_stages}
            disabled={readOnly}
            onCheckedChange={(v) => onChangeFlag({ allow_skip_stages: v })}
          />
          <PolicySwitch
            label="Allow reverting"
            description="An enquiry that has progressed can be moved back to an earlier stage."
            checked={draft.allow_revert}
            disabled={readOnly}
            onCheckedChange={(v) => onChangeFlag({ allow_revert: v })}
          />
          <PolicySwitch
            label="Duplicate detection"
            description="Warn when a new enquiry matches an existing one on phone / email."
            checked={draft.duplicate_detection}
            disabled={readOnly}
            onCheckedChange={(v) => onChangeFlag({ duplicate_detection: v })}
          />
        </div>

        {source === "tenant" && !readOnly && (
          <div className="border-border/60 mt-1 flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed px-3 py-2.5">
            <div className="grid gap-0.5">
              <span className="text-sm font-medium">{copy.resetTitle}</span>
              <span className="text-muted-foreground text-xs">{copy.resetBody}</span>
            </div>
            {confirmingReset ? (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmingReset(false)}
                  disabled={resetting}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setConfirmingReset(false);
                    onReset();
                  }}
                  disabled={resetting}
                >
                  {resetting ? (
                    <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <RotateCcw className="size-3.5" aria-hidden="true" />
                  )}
                  Confirm reset
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmingReset(true)}
                disabled={resetting}
              >
                <RotateCcw className="size-3.5" aria-hidden="true" />
                Reset to default
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PolicySwitch({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <label className="border-border/60 bg-muted/20 flex items-start gap-3 rounded-md border px-3 py-2.5">
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={label}
      />
      <div className="grid gap-0.5">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-muted-foreground text-xs">{description}</span>
      </div>
    </label>
  );
}

// ── Funnel chart card ─────────────────────────────────────────────────

interface FunnelDatum {
  stage_key: string;
  label: string;
  count: number;
  color: string;
  is_terminal: boolean;
  terminal_outcome: "admitted" | "rejected" | null;
}

function buildFunnelData(
  funnel: AdmissionFunnelResponse | undefined,
  draft: DraftConfig,
): FunnelDatum[] {
  const countByKey = new Map<string, number>();
  for (const s of funnel?.stages ?? []) countByKey.set(s.stage_key, s.count);

  return draft.stages.map((s, i) => ({
    stage_key: s.key ?? s.id,
    label: s.label || `Stage ${i + 1}`,
    count: s.key ? (countByKey.get(s.key) ?? 0) : 0,
    color: resolveStageColor({ color_hint: s.color_hint, order: i }),
    is_terminal: s.is_terminal,
    terminal_outcome: s.terminal_outcome,
  }));
}

function FunnelChartCard({
  funnel,
  loading,
  draft,
}: {
  funnel: AdmissionFunnelResponse | undefined;
  loading: boolean;
  draft: DraftConfig;
}) {
  const data = React.useMemo(() => buildFunnelData(funnel, draft), [funnel, draft]);
  const total = funnel?.total ?? 0;
  const height = Math.max(180, data.length * 36 + 32);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid gap-0.5">
            <CardTitle>Live funnel</CardTitle>
            <CardDescription>
              Enquiries currently sitting at each stage. Updates each time you save the funnel.
            </CardDescription>
          </div>
          <div className="grid place-items-end gap-0.5">
            <span className="text-foreground text-2xl font-semibold tabular-nums leading-none">
              {total.toLocaleString()}
            </span>
            <span className="text-muted-foreground text-[11px] uppercase tracking-wide">
              total enquiries
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="bg-muted/30 h-40 animate-pulse rounded" />
        ) : data.length === 0 ? (
          <p className="text-muted-foreground text-sm">No stages configured yet.</p>
        ) : (
          <div style={{ height }} className="w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RcBarChart
                data={data}
                layout="vertical"
                margin={{ top: 4, right: 32, bottom: 4, left: 8 }}
                barCategoryGap={6}
              >
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={140}
                  tickLine={false}
                  axisLine={false}
                  tick={{
                    fill: "var(--color-muted-foreground)",
                    fontSize: 12,
                    fontFamily: "inherit",
                  }}
                />
                <RcTooltip
                  cursor={{ fill: "var(--color-muted)", opacity: 0.4 }}
                  content={<FunnelTooltip />}
                />
                <Bar dataKey="count" radius={[4, 4, 4, 4]} animationDuration={300}>
                  {data.map((d) => (
                    <Cell key={d.stage_key} fill={d.color} />
                  ))}
                  <LabelList
                    dataKey="count"
                    position="right"
                    formatter={(v: number) => v.toLocaleString()}
                    style={{
                      fill: "var(--color-foreground)",
                      fontSize: 11,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  />
                </Bar>
              </RcBarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FunnelTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: FunnelDatum; value: number }>;
}) {
  if (!active || !payload?.length) return null;
  const datum = payload[0]!.payload;
  return (
    <div className="border-border bg-card/95 grid gap-1 rounded-lg border px-3 py-2 text-xs shadow-md backdrop-blur-sm">
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="size-2 shrink-0 rounded-full"
          style={{ background: datum.color }}
        />
        <span className="text-foreground font-medium">{datum.label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Enquiries</span>
        <span className="text-foreground ml-auto font-mono tabular-nums">
          {datum.count.toLocaleString()}
        </span>
      </div>
      {datum.is_terminal && datum.terminal_outcome ? (
        <Badge
          variant={datum.terminal_outcome === "admitted" ? "success" : "danger"}
          className="mt-0.5 w-fit text-[10px] uppercase"
        >
          {datum.terminal_outcome}
        </Badge>
      ) : null}
    </div>
  );
}

// ── Stages editor (dnd-kit sortable list) ─────────────────────────────

function StagesEditorCard({
  draft,
  setDraft,
  forms,
  formsLoading,
  readOnly,
  onAiAssist,
  onAiAssistRequirement,
}: {
  draft: DraftConfig;
  setDraft: React.Dispatch<React.SetStateAction<DraftConfig>>;
  forms: TenantFormSummary[];
  formsLoading: boolean;
  readOnly: boolean;
  onAiAssist?: () => void;
  onAiAssistRequirement?: () => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((d) => {
      const oldIdx = d.stages.findIndex((s) => s.id === active.id);
      const newIdx = d.stages.findIndex((s) => s.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return d;
      return { ...d, stages: arrayMove(d.stages, oldIdx, newIdx) };
    });
  };

  const updateStage = (id: string, patch: Partial<DraftStage>) => {
    setDraft((d) => ({
      ...d,
      stages: d.stages.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }));
  };

  const removeStage = (id: string) => {
    setDraft((d) => ({ ...d, stages: d.stages.filter((s) => s.id !== id) }));
  };

  const addStage = () => {
    setDraft((d) => {
      if (d.stages.length >= 10) return d;
      const idx = d.stages.length;
      const tempId = `__new_${Date.now().toString(36)}_${idx}`;
      const firstTerminalIdx = d.stages.findIndex((s) => s.is_terminal);
      const insertAt = firstTerminalIdx < 0 ? d.stages.length : firstTerminalIdx;
      const next: DraftStage = {
        id: tempId,
        key: null,
        label: "New stage",
        is_terminal: false,
        terminal_outcome: null,
        color_hint: null,
        auto_advance_days: null,
        required_documents: [],
        linked_form: null,
        requirements: [],
      };
      const stages = [...d.stages];
      stages.splice(insertAt, 0, next);
      return { ...d, stages };
    });
  };

  const list = (
    <ul className="grid gap-2">
      {draft.stages.map((stage, index) => (
        <SortableStageRow
          key={stage.id}
          stage={stage}
          index={index}
          forms={forms}
          formsLoading={formsLoading}
          readOnly={readOnly}
          onChange={(patch) => updateStage(stage.id, patch)}
          onRemove={() => removeStage(stage.id)}
        />
      ))}
    </ul>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid gap-0.5">
            <CardTitle>Stages ({draft.stages.length} of 10)</CardTitle>
            <CardDescription>
              {readOnly
                ? "A read-only view of your funnel stages."
                : 'Reorder by dragging the handle. Click a name to rename. At least one terminal "rejected" stage and exactly one terminal "admitted" stage are required.'}
            </CardDescription>
          </div>
          {!readOnly && (
            <div className="flex items-center gap-2">
              {onAiAssist && (
                <PromptInputButton
                  onClick={onAiAssist}
                  label="AI assist"
                  data-testid="ai-assist-stage"
                />
              )}
              {onAiAssistRequirement && (
                <PromptInputButton
                  onClick={onAiAssistRequirement}
                  label="AI requirement"
                  data-testid="ai-assist-requirement"
                />
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={addStage}
                disabled={draft.stages.length >= 10}
              >
                <Plus className="size-3.5" aria-hidden="true" />
                Add stage
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {readOnly ? (
          list
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={draft.stages.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {list}
            </SortableContext>
          </DndContext>
        )}
      </CardContent>
    </Card>
  );
}

function SortableStageRow({
  stage,
  index,
  forms,
  formsLoading,
  readOnly,
  onChange,
  onRemove,
}: {
  stage: DraftStage;
  index: number;
  forms: TenantFormSummary[];
  formsLoading: boolean;
  readOnly: boolean;
  onChange: (patch: Partial<DraftStage>) => void;
  onRemove: () => void;
}) {
  const sortable = useSortable({ id: stage.id, disabled: readOnly });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.6 : 1,
    zIndex: sortable.isDragging ? 10 : undefined,
  };

  const [editing, setEditing] = React.useState(false);
  const [labelDraft, setLabelDraft] = React.useState(stage.label);
  React.useEffect(() => setLabelDraft(stage.label), [stage.label]);

  const commitLabel = () => {
    setEditing(false);
    const trimmed = labelDraft.trim();
    if (trimmed === "" || trimmed === stage.label) {
      setLabelDraft(stage.label);
      return;
    }
    onChange({ label: trimmed });
  };

  const color = resolveStageColor({ color_hint: stage.color_hint, order: index });
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [reqsOpen, setReqsOpen] = React.useState(false);

  const requirementsCount = stage.requirements.length;
  const previewKindCount = stage.requirements.filter(
    (r) => !REQUIREMENT_KIND_LIVE_EVALUATOR[r.kind],
  ).length;

  return (
    <li
      ref={sortable.setNodeRef}
      style={style}
      className="border-border/60 bg-card data-[dragging=true]:border-primary/50 flex flex-wrap items-center gap-3 rounded-md border px-3 py-2.5 transition-shadow data-[dragging=true]:shadow-md"
      data-dragging={sortable.isDragging || undefined}
    >
      {!readOnly && (
        <button
          type="button"
          aria-label={`Drag to reorder ${stage.label}`}
          className="text-muted-foreground hover:bg-muted/60 hover:text-foreground -mx-1 cursor-grab rounded p-1 active:cursor-grabbing"
          {...sortable.attributes}
          {...sortable.listeners}
        >
          <GripVertical className="size-4" aria-hidden="true" />
        </button>
      )}

      <span
        aria-hidden="true"
        className="size-3 shrink-0 rounded-full ring-2 ring-inset ring-white/40 dark:ring-black/30"
        style={{ background: color }}
      />

      <div className="grid min-w-0 flex-1 gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          {editing && !readOnly ? (
            <Input
              autoFocus
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              onBlur={commitLabel}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitLabel();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setLabelDraft(stage.label);
                  setEditing(false);
                }
              }}
              className="h-7 max-w-[260px] text-sm"
              maxLength={64}
            />
          ) : readOnly ? (
            <span className="text-sm font-medium">{stage.label || `Stage ${index + 1}`}</span>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="hover:text-primary inline-flex items-center gap-1.5 text-left text-sm font-medium"
            >
              {stage.label || `Stage ${index + 1}`}
              <Pencil className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          )}
          <span className="text-muted-foreground font-mono text-[10px] uppercase">
            {stage.key ?? deriveStageKey(stage.label || `stage_${index + 1}`)}
            {stage.key === null ? " (new)" : ""}
          </span>
          {stage.is_terminal && stage.terminal_outcome === "admitted" && (
            <Badge variant="success" className="gap-1 text-[10px] uppercase">
              <CheckCircle2 className="size-3" aria-hidden="true" />
              Admitted
            </Badge>
          )}
          {stage.is_terminal && stage.terminal_outcome === "rejected" && (
            <Badge variant="danger" className="gap-1 text-[10px] uppercase">
              <XCircle className="size-3" aria-hidden="true" />
              Rejected
            </Badge>
          )}
        </div>

        <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-[11px]">
          {stage.linked_form ? (
            <button
              type="button"
              disabled={readOnly}
              className="hover:text-foreground border-border/60 inline-flex items-center gap-1.5 rounded border px-1.5 py-0.5 font-medium disabled:cursor-default disabled:opacity-100"
              onClick={() => !readOnly && setPickerOpen(true)}
            >
              <FileText className="size-3" aria-hidden="true" />
              {stage.linked_form.name}
              <span className="font-mono opacity-70">v{stage.linked_form.version}</span>
            </button>
          ) : readOnly ? (
            <span className="inline-flex items-center gap-1.5">
              <FileText className="size-3" aria-hidden="true" />
              No form linked
            </span>
          ) : (
            <button
              type="button"
              className="hover:text-foreground border-border/60 hover:border-border inline-flex items-center gap-1.5 rounded border border-dashed px-1.5 py-0.5"
              onClick={() => setPickerOpen(true)}
            >
              <FileText className="size-3" aria-hidden="true" />
              Link a form
            </button>
          )}
          {stage.required_documents.length > 0 && (
            <span>· {stage.required_documents.length} required document(s)</span>
          )}
          {readOnly ? (
            requirementsCount > 0 ? (
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="size-3" aria-hidden="true" />
                {requirementsCount} requirement{requirementsCount === 1 ? "" : "s"}
              </span>
            ) : null
          ) : (
            <button
              type="button"
              onClick={() => setReqsOpen(true)}
              disabled={stage.is_terminal}
              className="hover:text-foreground border-border/60 hover:border-border disabled:hover:text-muted-foreground disabled:hover:border-border/60 inline-flex items-center gap-1.5 rounded border border-dashed px-1.5 py-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              title={
                stage.is_terminal
                  ? "Terminal stages don't gate advance — requirements are not applicable."
                  : "Configure action-gated requirements for this stage."
              }
            >
              <ShieldCheck className="size-3" aria-hidden="true" />
              {requirementsCount === 0
                ? "Configure requirements"
                : `${requirementsCount} requirement${requirementsCount === 1 ? "" : "s"}`}
              {previewKindCount > 0 && (
                <span className="ml-0.5 inline-flex items-center gap-1 rounded-sm bg-amber-100 px-1 text-[10px] font-medium text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
                  <ClipboardList className="size-2.5" aria-hidden="true" />
                  {previewKindCount} preview
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {readOnly ? (
        <Badge variant="outline" className="text-[10px] uppercase">
          {stage.is_terminal
            ? stage.terminal_outcome === "admitted"
              ? "Terminal · Admitted"
              : "Terminal · Rejected"
            : "Intermediate"}
        </Badge>
      ) : (
        <Select
          value={
            stage.is_terminal
              ? stage.terminal_outcome === "admitted"
                ? "terminal_admitted"
                : "terminal_rejected"
              : "intermediate"
          }
          onValueChange={(v) => {
            if (v === "intermediate") {
              onChange({ is_terminal: false, terminal_outcome: null });
            } else if (v === "terminal_admitted") {
              onChange({ is_terminal: true, terminal_outcome: "admitted" });
            } else if (v === "terminal_rejected") {
              onChange({ is_terminal: true, terminal_outcome: "rejected" });
            }
          }}
        >
          <SelectTrigger className="h-8 w-[150px] text-xs" aria-label="Stage type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="intermediate">
              <span className="inline-flex items-center gap-2">
                <ArrowUpDown className="size-3" aria-hidden="true" />
                Intermediate
              </span>
            </SelectItem>
            <SelectItem value="terminal_admitted">
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="size-3 text-emerald-600" aria-hidden="true" />
                Terminal · Admitted
              </span>
            </SelectItem>
            <SelectItem value="terminal_rejected">
              <span className="inline-flex items-center gap-2">
                <XCircle className="size-3 text-rose-600" aria-hidden="true" />
                Terminal · Rejected
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      )}

      {!readOnly && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Remove ${stage.label}`}
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive size-8"
        >
          <Trash2 className="size-3.5" aria-hidden="true" />
        </Button>
      )}

      {pickerOpen && !readOnly && (
        <FormPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          stage={stage}
          forms={forms}
          formsLoading={formsLoading}
          onLinked={(linked) => {
            onChange({ linked_form: linked });
            setPickerOpen(false);
          }}
        />
      )}
      {reqsOpen && !readOnly && (
        <RequirementsDialog
          open={reqsOpen}
          onOpenChange={setReqsOpen}
          stage={stage}
          onCommit={(requirements) => {
            onChange({ requirements });
            setReqsOpen(false);
          }}
        />
      )}
    </li>
  );
}

// ── Form picker dialog ────────────────────────────────────────────────

function FormPickerDialog({
  open,
  onOpenChange,
  stage,
  forms,
  formsLoading,
  onLinked,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  stage: DraftStage;
  forms: TenantFormSummary[];
  formsLoading: boolean;
  onLinked: (linked: LinkedFormSummary | null) => void;
}) {
  const linkMutation = useLinkStageForm();
  const [query, setQuery] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const stageKey = stage.key;
  const canLink = stageKey !== null;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return forms;
    return forms.filter(
      (f) => f.name.toLowerCase().includes(q) || f.slug.toLowerCase().includes(q),
    );
  }, [forms, query]);

  const handlePick = (form: TenantFormSummary | null) => {
    if (!stageKey) {
      setError("Save the funnel first so this stage gets a key on the server, then link a form.");
      return;
    }
    setError(null);
    linkMutation.mutate(
      { stageKey, body: { form_slug: form?.slug ?? null } },
      {
        onSuccess: (res) => {
          const fresh = res.stages.find((s) => s.key === stageKey);
          onLinked(fresh?.linked_form ?? null);
        },
        onError: (e) => setError(e.message ?? "Failed to update form link."),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Link a form to {stage.label}</DialogTitle>
          <DialogDescription>
            The form renders inline when an enquiry reaches this stage. Each stage can have at most
            one active form — picking a new one replaces the previous link atomically.
          </DialogDescription>
        </DialogHeader>

        {!canLink && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            This is a new stage that hasn&apos;t been saved yet. Save the funnel first, then come
            back to link a form.
          </div>
        )}

        <div className="grid gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name or slug…"
            disabled={formsLoading}
          />

          <ul className="border-border/60 grid max-h-[280px] gap-1 overflow-y-auto rounded-md border p-1">
            {formsLoading ? (
              <li className="text-muted-foreground inline-flex items-center gap-2 px-2 py-2 text-xs">
                <Loader2 className="size-3 animate-spin" aria-hidden="true" />
                Loading forms…
              </li>
            ) : filtered.length === 0 ? (
              <li className="text-muted-foreground px-2 py-2 text-xs">
                {forms.length === 0
                  ? "No forms in your institution yet. Use the Forms editor to add some."
                  : "No forms match that filter."}
              </li>
            ) : (
              filtered.map((f) => {
                const isCurrent = stage.linked_form?.slug === f.slug;
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      disabled={!canLink || linkMutation.isPending}
                      onClick={() => handlePick(f)}
                      className="hover:bg-muted/60 data-[current=true]:bg-primary/10 data-[current=true]:ring-primary/40 flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-sm disabled:opacity-50 data-[current=true]:ring-1"
                      data-current={isCurrent || undefined}
                    >
                      <span className="grid min-w-0 gap-0.5">
                        <span className="truncate font-medium">{f.name}</span>
                        <span className="text-muted-foreground truncate text-[11px] tabular-nums">
                          Version {f.version} · {f.fields.length} field
                          {f.fields.length === 1 ? "" : "s"}
                        </span>
                      </span>
                      {isCurrent && (
                        <Badge variant="success" className="text-[10px] uppercase">
                          Linked
                        </Badge>
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>

          {error && (
            <p
              role="alert"
              className="text-destructive border-destructive/50 bg-destructive/5 rounded-md border px-3 py-2 text-xs"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!canLink || !stage.linked_form || linkMutation.isPending}
            onClick={() => handlePick(null)}
          >
            <Unlink className="size-3.5" aria-hidden="true" />
            Unlink current form
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Requirements dialog (Phase F3) ────────────────────────────────────

const REQUIREMENT_CONFIG_HINTS: Partial<
  Record<StageRequirementKind, Array<{ key: string; description: string; placeholder: string }>>
> = {
  manual_check: [
    {
      key: "slot",
      description: "Logical slot name (e.g. id_verification). Defaults to the requirement key.",
      placeholder: "id_verification",
    },
  ],
  document_upload: [
    {
      key: "slot",
      description: "Document slot name (e.g. birth_certificate). Required when F4 evaluator ships.",
      placeholder: "birth_certificate",
    },
  ],
  assessment_attempt: [
    {
      key: "template_slug",
      description: "Slug of the assessment template the applicant must attempt.",
      placeholder: "grade-6-entrance",
    },
    {
      key: "require_grading",
      description: "If true, attempt must be graded; if false, just attempted. Default: true.",
      placeholder: "true",
    },
  ],
  interview_completed: [
    {
      key: "template_slug",
      description: "Slug of the interview template that must be conducted.",
      placeholder: "panel-interview",
    },
  ],
};

function RequirementsDialog({
  open,
  onOpenChange,
  stage,
  onCommit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  stage: DraftStage;
  onCommit: (requirements: DraftRequirement[]) => void;
}) {
  const [drafts, setDrafts] = React.useState<DraftRequirement[]>(stage.requirements);
  React.useEffect(() => {
    if (open) setDrafts(stage.requirements);
  }, [open, stage.requirements]);

  const dirty = React.useMemo(
    () => draftRequirementsDirty(drafts, stage.requirements),
    [drafts, stage.requirements],
  );

  const validation = React.useMemo(() => validateRequirements(drafts), [drafts]);

  const update = (id: string, patch: Partial<DraftRequirement>) => {
    setDrafts((d) => d.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const remove = (id: string) => setDrafts((d) => d.filter((r) => r.id !== id));

  const add = () => {
    setDrafts((d) => [
      ...d,
      {
        id: `__new_${Date.now().toString(36)}_${d.length}`,
        key: null,
        kind: "form_submission",
        label: "",
        config: {},
      },
    ]);
  };

  const reorder = (id: string, direction: "up" | "down") => {
    setDrafts((d) => {
      const idx = d.findIndex((r) => r.id === id);
      const swapWith = direction === "up" ? idx - 1 : idx + 1;
      if (idx < 0 || swapWith < 0 || swapWith >= d.length) return d;
      const next = [...d];
      const [moving] = next.splice(idx, 1);
      next.splice(swapWith, 0, moving!);
      return next;
    });
  };

  const linkedFormSummary = stage.linked_form;
  const hasFormSubmissionWithoutLink =
    drafts.some((d) => d.kind === "form_submission") && !linkedFormSummary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure requirements · {stage.label}</DialogTitle>
          <DialogDescription>
            Action-gated checks every enquiry on this stage must clear before it can advance.
            Multiple requirements are AND-composed. Empty list keeps the legacy &quot;admin clicks →
            advance&quot; behaviour. Saves apply when you click Save funnel on the parent page.
          </DialogDescription>
        </DialogHeader>

        {hasFormSubmissionWithoutLink && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200"
          >
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>
              This stage has a <strong>form submission</strong> requirement but no form is linked
              yet. Use the <em>Link a form</em> chip on the stage row to attach the form the
              evaluator should look for, otherwise the BE will treat the requirement as PENDING for
              every enquiry.
            </span>
          </div>
        )}

        <ul className="grid gap-3">
          {drafts.length === 0 && (
            <li className="text-muted-foreground border-border/60 rounded-md border border-dashed px-3 py-6 text-center text-xs">
              No requirements yet. The stage advances whenever an admin clicks <code>advance</code>.
            </li>
          )}
          {drafts.map((req, i) => (
            <RequirementCard
              key={req.id}
              req={req}
              index={i}
              total={drafts.length}
              linkedFormSlug={linkedFormSummary?.slug ?? null}
              error={validation.errors[req.id]}
              onChange={(patch) => update(req.id, patch)}
              onRemove={() => remove(req.id)}
              onMoveUp={() => reorder(req.id, "up")}
              onMoveDown={() => reorder(req.id, "down")}
            />
          ))}
        </ul>

        <Button type="button" variant="outline" size="sm" onClick={add} className="w-fit">
          <Plus className="size-3.5" aria-hidden="true" />
          Add requirement
        </Button>

        {validation.summary && (
          <p
            role="alert"
            className="text-destructive border-destructive/50 bg-destructive/5 rounded-md border px-3 py-2 text-xs"
          >
            {validation.summary}
          </p>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setDrafts(stage.requirements);
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!dirty || validation.summary !== null}
            onClick={() => onCommit(drafts)}
          >
            <Save className="size-3.5" aria-hidden="true" />
            Apply requirements
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function draftRequirementsDirty(next: DraftRequirement[], prev: DraftRequirement[]): boolean {
  if (next.length !== prev.length) return true;
  for (let i = 0; i < next.length; i++) {
    const a = next[i]!;
    const b = prev[i]!;
    if (a.key !== b.key || a.kind !== b.kind || a.label !== b.label) return true;
    if (!shallowEqualConfig(a.config, b.config)) return true;
  }
  return false;
}

interface RequirementValidation {
  errors: Record<string, string>;
  summary: string | null;
}

function validateRequirements(reqs: DraftRequirement[]): RequirementValidation {
  const errors: Record<string, string> = {};
  const seenKeys = new Set<string>();
  for (const req of reqs) {
    const trimmedLabel = req.label.trim();
    if (!trimmedLabel) {
      errors[req.id] = "Label is required so the detail page knows what to render.";
      continue;
    }
    const effectiveKey =
      req.key ?? deriveRequirementKey(trimmedLabel || REQUIREMENT_KIND_LABELS[req.kind]);
    if (effectiveKey.length < 2) {
      errors[req.id] = "Requirement key must be at least 2 characters after slug-derivation.";
      continue;
    }
    if (seenKeys.has(effectiveKey)) {
      errors[req.id] =
        `Requirement key "${effectiveKey}" is already used on this stage. Rename to differentiate.`;
      continue;
    }
    seenKeys.add(effectiveKey);
  }
  const summary =
    Object.keys(errors).length === 0
      ? null
      : `Resolve ${Object.keys(errors).length} requirement issue${
          Object.keys(errors).length === 1 ? "" : "s"
        } before applying.`;
  return { errors, summary };
}

function RequirementCard({
  req,
  index,
  total,
  linkedFormSlug,
  error,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  req: DraftRequirement;
  index: number;
  total: number;
  linkedFormSlug: string | null;
  error: string | undefined;
  onChange: (patch: Partial<DraftRequirement>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const live = REQUIREMENT_KIND_LIVE_EVALUATOR[req.kind];
  const helpText = REQUIREMENT_KIND_HELP[req.kind];
  const hints = REQUIREMENT_CONFIG_HINTS[req.kind] ?? [];
  const effectiveKey =
    req.key ?? deriveRequirementKey(req.label.trim() || REQUIREMENT_KIND_LABELS[req.kind]);

  return (
    <li className="border-border/60 bg-card/60 grid gap-3 rounded-md border px-3 py-2.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="grid min-w-0 flex-1 gap-1.5">
          <div className="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_180px]">
            <label className="grid gap-1 text-xs">
              <span className="text-foreground font-medium">Label</span>
              <Input
                value={req.label}
                onChange={(e) => onChange({ label: e.target.value })}
                placeholder={REQUIREMENT_KIND_LABELS[req.kind]}
                maxLength={120}
                className="h-8 text-sm"
                aria-invalid={error ? "true" : undefined}
              />
              <span className="text-muted-foreground font-mono text-[10px]">
                {req.key ? `key=${req.key}` : `derived key → ${effectiveKey}`}
                {req.key === null ? " (new)" : null}
              </span>
            </label>

            <label className="grid gap-1 text-xs">
              <span className="text-foreground font-medium">Kind</span>
              <Select
                value={req.kind}
                onValueChange={(v) => onChange({ kind: v as StageRequirementKind, config: {} })}
                disabled={req.key !== null}
              >
                <SelectTrigger className="h-8 text-sm" aria-label="Requirement kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(REQUIREMENT_KIND_LABELS) as StageRequirementKind[]).map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      <span className="inline-flex items-center gap-2">
                        {REQUIREMENT_KIND_LABELS[kind]}
                        {!REQUIREMENT_KIND_LIVE_EVALUATOR[kind] && (
                          <Badge
                            variant="outline"
                            className="h-4 border-amber-300 px-1 text-[9px] uppercase text-amber-900 dark:border-amber-900/60 dark:text-amber-200"
                          >
                            preview
                          </Badge>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {req.key !== null && (
                <span className="text-muted-foreground text-[10px]">
                  Kind is locked once persisted (BE immutability guard).
                </span>
              )}
            </label>
          </div>

          <p className="text-muted-foreground text-[11px] leading-relaxed">{helpText}</p>

          {req.kind === "form_submission" && (
            <p className="text-muted-foreground text-[11px]">
              {linkedFormSlug
                ? `Evaluator looks for a saved payload on the linked form "${linkedFormSlug}".`
                : "No form is linked to this stage yet — link one before saving so the evaluator has something to check."}
            </p>
          )}

          {hints.length > 0 && (
            <div className="grid gap-1.5">
              {hints.map((hint) => (
                <ConfigField
                  key={hint.key}
                  label={hint.key}
                  description={hint.description}
                  placeholder={hint.placeholder}
                  value={configValueAsString(req.config[hint.key])}
                  onChange={(v) =>
                    onChange({
                      config: parseConfigPatch(req.config, hint.key, v),
                    })
                  }
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={index === 0}
            onClick={onMoveUp}
            aria-label="Move requirement up"
          >
            <ArrowUpDown className="size-3 -rotate-90" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={index === total - 1}
            onClick={onMoveDown}
            aria-label="Move requirement down"
          >
            <ArrowUpDown className="size-3 rotate-90" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Remove requirement"
            onClick={onRemove}
            className="text-muted-foreground hover:text-destructive size-7"
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {!live && (
        <Badge
          variant="outline"
          className="w-fit gap-1 border-amber-300 text-[10px] uppercase text-amber-900 dark:border-amber-900/60 dark:text-amber-200"
        >
          <ClipboardList className="size-3" aria-hidden="true" />
          Evaluator pending — applicants see &quot;Pending&quot; until the F-slice ships
        </Badge>
      )}

      {error && (
        <p
          role="alert"
          className="text-destructive border-destructive/50 bg-destructive/5 rounded-md border px-2 py-1 text-[11px]"
        >
          {error}
        </p>
      )}
    </li>
  );
}

function ConfigField({
  label,
  description,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  description: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="grid gap-1 text-xs">
      <span className="text-foreground font-mono text-[10px] uppercase tracking-wide">{label}</span>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-7 text-sm"
      />
      <span className="text-muted-foreground text-[10px] leading-snug">{description}</span>
    </label>
  );
}

function configValueAsString(v: unknown): string {
  if (v === undefined || v === null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "boolean" || typeof v === "number") return String(v);
  return JSON.stringify(v);
}

function parseConfigPatch(
  current: Record<string, unknown>,
  key: string,
  raw: string,
): Record<string, unknown> {
  const next = { ...current };
  const trimmed = raw.trim();
  if (trimmed === "") {
    delete next[key];
    return next;
  }
  if (trimmed === "true" || trimmed === "false") {
    next[key] = trimmed === "true";
    return next;
  }
  if (/^-?\d+$/.test(trimmed)) {
    next[key] = Number.parseInt(trimmed, 10);
    return next;
  }
  if (/^-?\d+\.\d+$/.test(trimmed)) {
    next[key] = Number.parseFloat(trimmed);
    return next;
  }
  next[key] = trimmed;
  return next;
}

// ── Save bar ──────────────────────────────────────────────────────────

function SaveBar({
  dirty,
  saving,
  onSave,
  onDiscard,
  error,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  error: string | null;
}) {
  if (!dirty && !error) return null;
  return (
    <div className="border-border/60 bg-card/95 sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5 shadow-sm backdrop-blur-sm">
      <div className="grid gap-0.5">
        <span className="text-sm font-medium">{dirty ? "Unsaved changes" : "Save failed"}</span>
        <span className="text-muted-foreground text-xs">
          {dirty
            ? "Funnel updates apply immediately to new enquiries once saved."
            : "Resolve the error below and try again."}
        </span>
        {error && (
          <span
            role="alert"
            className="text-destructive mt-1 inline-flex items-center gap-1.5 text-xs"
          >
            <AlertTriangle className="size-3" aria-hidden="true" />
            {error}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
        <Button type="button" size="sm" onClick={onSave} disabled={!dirty || saving}>
          {saving ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="size-3.5" aria-hidden="true" />
          )}
          Save funnel
        </Button>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

interface TenantFormSummary {
  id: string;
  slug: string;
  name: string;
  version: number;
  fields: Array<{ key: string }>;
}

function toFormSummary(form: FormDefinition): TenantFormSummary {
  return {
    id: form.id,
    slug: form.slug,
    name: form.name,
    version: form.version,
    fields: form.fields.map((f) => ({ key: f.key })),
  };
}

function isDirty(draft: DraftConfig, initial: AdmissionStagesResponse): boolean {
  if (
    draft.allow_direct_admission !== initial.allow_direct_admission ||
    draft.allow_skip_stages !== initial.allow_skip_stages ||
    draft.allow_revert !== initial.allow_revert ||
    draft.duplicate_detection !== initial.duplicate_detection
  ) {
    return true;
  }
  if (draft.stages.length !== initial.stages.length) return true;
  for (let i = 0; i < draft.stages.length; i++) {
    const d = draft.stages[i]!;
    const o = initial.stages[i]!;
    if (
      d.key !== o.key ||
      d.label !== o.label ||
      d.is_terminal !== o.is_terminal ||
      d.terminal_outcome !== o.terminal_outcome ||
      (d.color_hint ?? null) !== (o.color_hint ?? null) ||
      (d.auto_advance_days ?? null) !== (o.auto_advance_days ?? null) ||
      d.required_documents.length !== o.required_documents.length ||
      d.required_documents.some((doc, j) => doc !== o.required_documents[j]) ||
      requirementsDirty(d.requirements, o.requirements)
    ) {
      return true;
    }
  }
  return false;
}

function requirementsDirty(draft: DraftRequirement[], baseline: StageRequirementWire[]): boolean {
  if (draft.length !== baseline.length) return true;
  for (let i = 0; i < draft.length; i++) {
    const d = draft[i]!;
    const o = baseline[i]!;
    if (d.key !== o.key || d.kind !== o.kind || d.label !== o.label) return true;
    if (!shallowEqualConfig(d.config, o.config)) return true;
  }
  return false;
}

function shallowEqualConfig(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function buildWriteBody(draft: DraftConfig) {
  const stages: WriteAdmissionStageBody[] = draft.stages.map((s, i) => ({
    key: s.key ?? deriveUniqueKey(s, i, draft.stages),
    label: s.label.trim() || `Stage ${i + 1}`,
    order: i + 1,
    is_terminal: s.is_terminal,
    terminal_outcome: s.is_terminal ? s.terminal_outcome : null,
    color_hint: s.color_hint,
    auto_advance_days: s.auto_advance_days,
    required_documents: s.required_documents,
    requirements: s.requirements.map((req, j) => ({
      key: req.key ?? deriveUniqueRequirementKey(req, j, s.requirements),
      kind: req.kind,
      label: req.label.trim() || REQUIREMENT_KIND_LABELS[req.kind],
      config: req.config,
    })),
  }));
  return {
    stages,
    allow_direct_admission: draft.allow_direct_admission,
    allow_skip_stages: draft.allow_skip_stages,
    allow_revert: draft.allow_revert,
    duplicate_detection: draft.duplicate_detection,
  };
}

function deriveUniqueRequirementKey(
  req: DraftRequirement,
  index: number,
  all: DraftRequirement[],
): string {
  const seed = req.label.trim() || REQUIREMENT_KIND_LABELS[req.kind];
  const base = deriveRequirementKey(seed);
  const taken = new Set(
    all.filter((r, i) => i !== index && r.key !== null).map((r) => r.key as string),
  );
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

function deriveUniqueKey(stage: DraftStage, index: number, all: DraftStage[]): string {
  const base = deriveStageKey(stage.label || `stage_${index + 1}`);
  const taken = new Set(
    all.filter((s, i) => i !== index && s.key !== null).map((s) => s.key as string),
  );
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}
